import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useParty } from '../contexts/PartyContext';
import { useAuth } from '../contexts/AuthContext';
import { useYouTubeSearch } from '../hooks/useYouTubeSearch';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, onSnapshot, QueryDocumentSnapshot } from 'firebase/firestore';
import { functions, db } from '../firebase';
import { useViewerIdentity } from '../hooks/useViewerIdentity';
import { useHostBranding } from '../hooks/useHostBranding';
import { useToast } from '../contexts/ToastContext';
import AnalyticsService from '../services/AnalyticsService';
import SingerHistoryService from '../services/SingerHistoryService';
import SingerHistoryPanel from '../components/SingerHistoryPanel';
import { addReducedQueueSong, joinReducedParty, mapPartySnapshot } from '../services/reducedPartyActions';
import { reducedModeEnabled, reducedModeYouTubeEnabled } from '../utils/reducedMode';
// Virtualization is loaded dynamically to avoid bundler/ESM interop issues

const SearchPage: React.FC = () => {
  const { partyId } = useParams<{ partyId: string }>();
  const navigate = useNavigate();
  const { currentParty, songQueue, setCurrentParty } = useParty();
  const { user } = useAuth();
  const { toast } = useToast();
  const { viewerId } = useViewerIdentity();
  const isHost = !!(user && currentParty && user.uid === (currentParty as any).createdBy);
  
  const [search, setSearch] = useState('');
  const [singerName, setSingerName] = useState(() => {
    // Load saved singer name from localStorage
    return localStorage.getItem('singerName') || '';
  });
  const [source, setSource] = useState<'youtube'|'local'>(reducedModeEnabled && !reducedModeYouTubeEnabled ? 'local' : 'youtube');
  const [activeTab, setActiveTab] = useState<'search' | 'trending' | 'recommendations'>('search');
  const [trendingSongs, setTrendingSongs] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [loadingTrending, setLoadingTrending] = useState(false);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [localCatalog, setLocalCatalog] = useState<Array<{ id: string; title: string; artist?: string | null; durationSeconds?: number | null }>>([]);
  const [localSelected, setLocalSelected] = useState<Record<string, boolean>>({});
  const [localSortKey, setLocalSortKey] = useState<'title'|'artist'>('title');
  const [localSortOrder, setLocalSortOrder] = useState<'asc'|'desc'>('asc');
  const [groupingMode, setGroupingMode] = useState<'none'|'letter'|'artistTitle'>('letter');
  const listRef = useRef<any>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  // Virtual list sticky header support
  const [currentSectionKey, setCurrentSectionKey] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  // Resolve virtualization component at runtime to avoid type/packaging mismatches
  const [VirtualList, setVirtualList] = useState<any>(null);
  useEffect(() => {
    let mounted = true;
    import('react-window')
      .then((mod: any) => {
        if (!mounted) return;
        const Comp = mod?.FixedSizeList ?? mod?.default?.FixedSizeList ?? null;
        setVirtualList(Comp || null);
      })
      .catch(() => setVirtualList(null));
    return () => {
      mounted = false;
    };
  }, []);

  // Only show mic UI if party is mic-enabled
  const micEnabled = !!(currentParty && (currentParty.settings as any)?.phoneMicEnabled);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isPartyLoading, setIsPartyLoading] = useState(false);
  const [partyError, setPartyError] = useState<string | null>(null);
  
  const { searchResults, isLoading, error, searchYouTube, clearResults } = useYouTubeSearch();
  const hostUserId = currentParty?.createdBy;
  const { branding: hostBranding } = useHostBranding(hostUserId);

  useEffect(() => {
    if (!reducedModeEnabled) return;
    setSource(reducedModeYouTubeEnabled ? 'youtube' : 'local');
    setActiveTab('search');
  }, []);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Subscribe to local catalog when source=local
  useEffect(() => {
    if (!partyId || source !== 'local') return;
    const colRef = collection(db, 'parties', partyId, 'localCatalog');
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const list = snap.docs.map((d: QueryDocumentSnapshot<any>) => ({ id: d.id, title: d.data().title, artist: d.data().artist ?? null, durationSeconds: d.data().durationSeconds ?? null }));
        setLocalCatalog(list);
        setLocalError(null);
      },
      (err) => {
        setLocalError(err?.message || 'Failed to load catalog');
        setLocalCatalog([]);
      }
    );
    return () => unsub();
  }, [partyId, source]);

  const loadParty = useCallback(async () => {
    if (!partyId) return;
    setIsPartyLoading(true);
    setPartyError(null);
    try {
      if (partyId.startsWith('demo-')) {
        setCurrentParty({
          id: partyId,
          name: 'Demo Karaoke Party',
          createdBy: 'demo-user',
          createdAt: new Date(),
          settings: {
            maxQueueSize: 50,
            allowDuplicates: false,
            requireAuth: false
          },
          currentSong: null,
          isActive: true
        } as any);
        return;
      }

      if (reducedModeEnabled) {
        const result = await joinReducedParty(partyId);
        setCurrentParty(result.party as any);
        setPartyError(null);
      } else {
        const joinPartyFunction = httpsCallable(functions, 'party');
        await joinPartyFunction({
          action: 'join',
          partyId
        });

        const partySnap = await getDoc(doc(db, 'parties', partyId));
        if (partySnap.exists()) {
          const partyData = partySnap.data() as any;
          setCurrentParty(mapPartySnapshot(partySnap.id, partyData) as any);
          setPartyError(null);
        } else {
          setPartyError('Party not found');
        }
      }
    } catch (error: any) {
      setPartyError(error?.message || 'Failed to load party');
    } finally {
      setIsPartyLoading(false);
    }
  }, [partyId, setCurrentParty]);

  useEffect(() => {
    if (!partyId) {
      setPartyError('Missing party ID');
      return;
    }
    if (!currentParty || currentParty.id !== partyId) {
      void loadParty();
    }
  }, [partyId, currentParty?.id, loadParty]);

  const handleSearch = () => {
    if (!search.trim()) {
      clearResults();
      return;
    }
  if (source === 'youtube') {
    // Track search analytics
    AnalyticsService.trackSongSearch(search.trim(), 'youtube', user?.uid);
    searchYouTube(search.trim(), partyId);
  }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const loadTrendingSongs = async () => {
    setLoadingTrending(true);
    try {
      const trending = await AnalyticsService.getTrendingSongs(20);
      setTrendingSongs(trending);
    } catch (error) {
      console.error('Failed to load trending songs:', error);
    } finally {
      setLoadingTrending(false);
    }
  };

  const loadRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      // Get user's history to base recommendations on
      const history = await SingerHistoryService.getHistory(user?.uid, 50);
      const songIds = history.map(h => h.songId);
      const recs = await AnalyticsService.getRecommendations(songIds, 20);
      setRecommendations(recs);
    } catch (error) {
      console.error('Failed to load recommendations:', error);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  // Load trending/recommendations when switching tabs
  useEffect(() => {
    if (reducedModeEnabled && activeTab !== 'search') {
      setActiveTab('search');
      return;
    }
    if (activeTab === 'trending' && trendingSongs.length === 0) {
      loadTrendingSongs();
    } else if (activeTab === 'recommendations' && recommendations.length === 0) {
      loadRecommendations();
    }
  }, [activeTab]);

  const handleAddSong = async (song: any) => {
    if (!partyId) return;
    
    const finalSingerName = singerName.trim() || 'Unknown Singer';
    
    // Handle demo parties differently
    if (partyId.startsWith('demo-')) {
      // For demo, just show success message
      clearResults();
      setSearch('');
      alert(`"${song.title}" would be added to queue for ${finalSingerName}! (Demo mode - no actual functionality)`);
      return;
    }
    
    try {
      if (!viewerId) {
        toast('Preparing your device identity. Please try again in a moment.', 'info');
        return;
      }
      const normalizedArtist = (song?.artist || song?.channelTitle || 'Unknown Artist') as string;
      const payloadSong = source === 'youtube'
        ? { ...song, videoId: song.videoId || song.id, singerName: finalSingerName, source: 'youtube', artist: normalizedArtist }
        : { ...song, singerName: finalSingerName, source: 'local', artist: normalizedArtist };
      if (reducedModeEnabled) {
        await addReducedQueueSong(partyId, payloadSong, finalSingerName, viewerId);
      } else {
        const addSongFunction = httpsCallable(functions, 'party');
        await addSongFunction({
          action: 'queue',
          partyId,
          queueAction: 'add',
          song: payloadSong,
          viewerId
        });
      }
      
      // Save singer name to localStorage for next time
      localStorage.setItem('singerName', finalSingerName);
      
      // Track analytics and save to history
      AnalyticsService.trackSongAdded(payloadSong, partyId!, finalSingerName, user?.uid);
      SingerHistoryService.addToHistory(payloadSong, finalSingerName, user?.uid);
      
      // Clear search results after adding
      clearResults();
      setSearch('');
      
      // Show success feedback
      toast(`"${song.title}" added to queue for ${finalSingerName}!`, 'success');
    } catch (error) {
      toast('Failed to add song to queue. Please try again.', 'error');
    }
  };

  const handleAddLocalSingle = (entry: { id: string; title: string; artist?: string | null }) => {
    return handleAddSong({ id: entry.id, title: entry.title, artist: entry.artist || 'Unknown Artist', localId: entry.id });
  };

  const handleAddLocalSelected = async () => {
    const ids = Object.entries(localSelected).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) {
      toast('Select at least one song.', 'warning');
      return;
    }
    setShowConfirm(true);
  };

  const confirmAddLocalSelected = async () => {
    const ids = Object.entries(localSelected).filter(([, v]) => v).map(([k]) => k);
    for (const id of ids) {
      const entry = localCatalog.find(e => e.id === id);
      if (entry) await handleAddLocalSingle(entry!);
    }
    setShowConfirm(false);
    setLocalSelected({});
  };

  const formattedDuration = (s?: number | null) => {
    if (!s && s !== 0) return '';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const filteredSortedLocal = localCatalog
    .filter(e => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return e.title.toLowerCase().includes(q) || (e.artist?.toLowerCase().includes(q) ?? false);
    })
    .sort((a,b) => {
      // For artistTitle grouping, force comparator to artist then title
      if (groupingMode === 'artistTitle') {
        const ka = (a.artist || '').toLowerCase();
        const kb = (b.artist || '').toLowerCase();
        const primary = ka.localeCompare(kb);
        const secondary = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
        const cmp = primary || secondary;
        return localSortOrder === 'asc' ? cmp : -cmp;
      }
      const keyA = (localSortKey === 'artist' ? (a.artist || '') : a.title).toLowerCase();
      const keyB = (localSortKey === 'artist' ? (b.artist || '') : b.title).toLowerCase();
      const cmp = keyA.localeCompare(keyB) || a.title.toLowerCase().localeCompare(b.title.toLowerCase());
      return localSortOrder === 'asc' ? cmp : -cmp;
    });

  // Build grouped sections
  const toLetter = (s: string) => {
    const c = s.trim().charAt(0).toUpperCase();
    return /^[A-Z]$/.test(c) ? c : '#';
  };
  const sections = (() => {
    if (groupingMode === 'none') {
      return [{ key: 'ALL', items: filteredSortedLocal }];
    }
    if (groupingMode === 'artistTitle') {
      const map = new Map<string, typeof filteredSortedLocal>();
      for (const e of filteredSortedLocal) {
        const k = toLetter((e.artist || '')); 
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(e);
      }
      return Array.from(map.entries())
        .sort((a,b) => a[0].localeCompare(b[0]))
        .map(([k, items]) => ({ key: k, items }));
    }
    // letter grouping by current sort key
    const map = new Map<string, typeof filteredSortedLocal>();
    for (const e of filteredSortedLocal) {
      const k = toLetter(localSortKey === 'artist' ? (e.artist || '') : e.title);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(e);
    }
    return Array.from(map.entries())
      .sort((a,b) => a[0].localeCompare(b[0]))
      .map(([k, items]) => ({ key: k, items }));
  })();

  // Flatten grouped sections into a single row list for virtualization
  type Row =
    | { kind: 'header'; key: string }
    | { kind: 'item'; entry: { id: string; title: string; artist?: string | null; durationSeconds?: number | null } };

  const { rows, headerIndexMap } = useMemo(() => {
    const out: Row[] = [];
    const headerMap = new Map<string, number>();
    for (const section of sections) {
      // Add header row
      headerMap.set(section.key, out.length);
      out.push({ kind: 'header', key: section.key });
      // Add items
      for (const e of section.items) {
        out.push({ kind: 'item', entry: e });
      }
    }
    return { rows: out, headerIndexMap: headerMap };
  }, [sections]);

  // Fixed item size for virtualization simplicity
  const ITEM_SIZE = 60; // px

  const jumpToLetter = (letter: string) => {
    // Find section by letter key and scroll to its header index
    const isLetter = /^[A-Z]$/.test(letter);
    const key = isLetter ? letter : '#';
    const idx = headerIndexMap.get(key);
    if (idx != null && listRef.current && 'scrollToItem' in listRef.current) {
      (listRef.current as any).scrollToItem(idx, 'start');
    }
  };

  const onItemsRendered = useCallback(({ visibleStartIndex }: { visibleStartIndex: number }) => {
    // Determine the current section header for sticky overlay
    let i = visibleStartIndex;
    while (i >= 0) {
      const row = rows[i];
      if (!row) break;
      if (row.kind === 'header') {
        setCurrentSectionKey(row.key);
        return;
      }
      i--;
    }
    setCurrentSectionKey(null);
  }, [rows]);

  const handleConnectYouTube = async () => {
    try {
      if (!user) {
        toast('Please sign in to connect YouTube', 'warning');
        return;
      }
      const token = await user.getIdToken();
      const res = await fetch('https://us-central1-qroke-a6fed.cloudfunctions.net/youtubeAuthStartFn', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to start YouTube auth');
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast('Failed to get YouTube auth URL', 'error');
      }
    } catch (e: any) {
      toast(e.message || 'Failed to connect YouTube', 'error');
    }
  };

  if (isPartyLoading || (!currentParty && partyError === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-qr-light">
        <div className="text-center text-qr-dark">
          <div className="text-5xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold mb-2">Connecting to party...</h2>
          <p className="text-gray-700">Grabbing the latest queue and settings.</p>
        </div>
      </div>
    );
  }

  if (!currentParty) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-qr-light">
        <div className="text-center text-qr-dark">
          <h1 className="text-2xl font-bold mb-4">Party Not Found</h1>
          {partyError && <p className="mb-4 text-gray-700">{partyError}</p>}
          <button
            onClick={() => navigate('/')}
            className="bg-qr-primary hover:bg-qr-secondary px-6 py-2 rounded-lg text-white font-medium transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const pageBackgroundStyle = hostBranding?.backgroundUrl
    ? { backgroundImage: `url(${hostBranding.backgroundUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : undefined;
  const pageFontStyle = hostBranding?.fontFamily ? { fontFamily: hostBranding.fontFamily } : undefined;

  return (
    <div className="min-h-screen bg-qr-light text-qr-dark p-6 relative" style={{ ...(pageBackgroundStyle || {}), ...(pageFontStyle || {}) }}>
      {hostBranding?.backgroundUrl && <div className="absolute inset-0 bg-black/40" />}
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          {hostBranding?.logoUrl ? (
            <img src={hostBranding.logoUrl} alt={hostBranding.venueName || 'Venue Logo'} className="h-20 mx-auto mb-4 object-contain" />
          ) : (
            <img src="/Qroke Logo.png" alt="QrOke Logo" className="h-20 mx-auto mb-4" />
          )}
          <h1 className="text-4xl font-display font-bold mb-2">🎤 Song Request</h1>
          <h2 className="text-xl text-gray-700">{currentParty.name}</h2>
          <button
            onClick={() => navigate(`/party/${partyId}`)}
            className="mt-4 bg-white hover:bg-qr-surface border border-gray-200 px-4 py-2 rounded-lg transition-colors"
          >
            ← Back to Party
          </button>
        </div>

        {/* Singer History Panel */}
        <SingerHistoryPanel
          userId={user?.uid}
          onSelectSong={async (song) => {
            if (!singerName.trim()) {
              toast('Please enter your name first!', 'warning');
              return;
            }
            // Auto-add the selected historical song
            await handleAddSong(song);
          }}
          className="mb-6"
        />

        {/* Singer Name Input (required for all parties) */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
          <label className="block text-lg font-semibold mb-2 text-qr-dark">Singer Name:</label>
          <input
            type="text"
            value={singerName}
            onChange={(e) => setSingerName(e.target.value)}
            placeholder="Enter your name"
            aria-invalid={!singerName.trim()}
            className={`w-full p-3 text-lg rounded-lg bg-qr-light text-qr-dark border focus:outline-none ${singerName.trim() ? 'border-gray-300 focus:border-qr-primary' : 'border-red-500 focus:border-red-400'}`}
          />
          {!singerName.trim() && (
            <p className="mt-2 text-sm text-red-500">Please enter your name before adding a song to the queue.</p>
          )}
        </div>

        {/* Tab Selector */}
        <div className="bg-white border border-gray-200 rounded-2xl p-2 mb-6 flex gap-2">
          <button
            onClick={() => setActiveTab('search')}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors ${
              activeTab === 'search'
                ? 'bg-qr-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            🔍 Search
          </button>
          {(!reducedModeEnabled || reducedModeYouTubeEnabled) && (
            <button
              onClick={() => setActiveTab('trending')}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors ${
                activeTab === 'trending'
                  ? 'bg-qr-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔥 Trending
            </button>
          )}
          {(!reducedModeEnabled || reducedModeYouTubeEnabled) && (
            <button
              onClick={() => setActiveTab('recommendations')}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors ${
                activeTab === 'recommendations'
                  ? 'bg-qr-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ✨ For You
            </button>
          )}
        </div>

        {/* Source selector + Search / Local Catalog */}
        {activeTab === 'search' && (
        <div className="bg-qr-surface border border-qr-primary/30 rounded-2xl p-6 mb-6">
          {reducedModeEnabled && !reducedModeYouTubeEnabled && (
            <div className="mb-4 rounded-2xl border border-stone-200 bg-stone-50 px-4 py-4 text-sm text-stone-800">
              <div className="mb-2 font-semibold">Local catalog first</div>
              If the host has published a local catalog, you can browse and queue from it without extra setup.
            </div>
          )}
          {reducedModeEnabled && reducedModeYouTubeEnabled && (
            <div className="mb-4 rounded-2xl border border-stone-200 bg-stone-50 px-4 py-4 text-sm text-stone-800">
              <div className="mb-2 font-semibold">Lightweight YouTube search</div>
              Search stays available through a lightweight browser-side YouTube lookup, with the local catalog still available when the host has one.
            </div>
          )}
          <div className="flex items-center gap-2 mb-4">
            {(!reducedModeEnabled || reducedModeYouTubeEnabled) && (
              <label className={`px-3 py-2 rounded-lg cursor-pointer text-sm ${source==='youtube'?'bg-qr-primary text-white':'bg-white/50 text-qr-dark'}`}
                onClick={() => setSource('youtube')}>YouTube</label>
            )}
            <label className={`px-3 py-2 rounded-lg cursor-pointer text-sm ${source==='local'?'bg-qr-primary text-white':'bg-white/50 text-qr-dark'}`}
              onClick={() => setSource('local')}>Local</label>
          </div>
          <div className="flex gap-3 mb-4">
            {source === 'youtube' ? (
              <>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search for songs on YouTube..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className="flex-1 p-3 text-lg rounded-lg bg-white text-qr-dark border border-gray-300 focus:border-qr-primary focus:outline-none"
                />
                <button
                  onClick={handleSearch}
                  disabled={isLoading}
                  className="bg-qr-accent hover:bg-purple-500 disabled:bg-gray-400 px-6 py-3 rounded-lg text-white font-semibold transition-colors"
                  style={hostBranding?.primaryColor ? { backgroundColor: hostBranding.primaryColor } : undefined}
                >
                  {isLoading ? 'Searching...' : 'Search'}
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Filter local catalog..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 p-3 text-lg rounded-lg bg-white text-qr-dark border border-gray-300 focus:border-qr-primary focus:outline-none"
                />
                <button
                  onClick={handleAddLocalSelected}
                  className="bg-qr-primary hover:bg-qr-secondary px-6 py-3 rounded-lg text-white font-semibold transition-colors"
                  disabled={!singerName.trim()}
                >
                  Add Selected
                </button>
              </>
            )}
          </div>

          {/* Streaming use disclaimer */}
          {(!reducedModeEnabled || reducedModeYouTubeEnabled) && (
            <div className="mb-4 text-xs text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-md px-3 py-2">
              YouTube integration is for personal, at‑home use only. Commercial streaming may violate provider terms and copyright law.
              <button onClick={() => navigate('/content-sources')} className="ml-2 underline">Learn more</button>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 rounded-lg p-4 mb-4">
              <p className="mb-2">{error}</p>
              {isHost && error.toLowerCase().includes('youtube connection required') && (
                <button
                  onClick={handleConnectYouTube}
                  className="bg-white hover:bg-qr-surface border border-gray-200 text-qr-dark font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  Connect YouTube (Host)
                </button>
              )}
            </div>
          )}

          {/* Loading Spinner */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-8 space-y-3">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-qr-primary"></div>
              <p className="text-gray-600 font-medium">Searching for songs...</p>
            </div>
          )}

          {/* Search Results or Local Catalog */}
          {!isLoading && source === 'youtube' && searchResults.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Search Results:</h3>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {searchResults.map((song) => (
                  <div key={song.id} className="flex items-center space-x-4 p-4 bg-white rounded-lg border border-gray-200 hover:bg-qr-surface transition-colors">
                    {song.thumbnail && (
                      <img 
                        src={song.thumbnail} 
                        alt={song.title}
                        className="w-16 h-16 rounded object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-semibold text-qr-dark">{song.title}</h4>
                      <p className="text-gray-600 text-sm">{song.channelTitle}</p>
                      {song.duration && (
                        <p className="text-gray-500 text-xs">Duration: {song.duration}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleAddSong(song)}
                      disabled={!singerName.trim()}
                      className="bg-qr-primary hover:bg-qr-secondary disabled:bg-gray-300 px-4 py-2 rounded-lg text-white font-semibold transition-colors"
                      style={singerName.trim() && hostBranding?.primaryColor ? { backgroundColor: hostBranding.primaryColor } : undefined}
                    >
                      Add to Queue
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {source === 'local' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Local Catalog:</h3>
              {localCatalog.length === 0 ? (
                <div className="text-gray-700 bg-white/70 rounded p-4">
                  {isHost ? 'No catalog published yet. Open Host Controls → Sources and Connect Folder, then Publish Catalog.' : 'Catalog not available yet. Please check back soon.'}
                </div>
              ) : (
                <>
                  {/* Sort + Jump bar */}
                  <div className="flex items-center justify-between bg-white rounded px-3 py-2 border border-gray-200">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-700">Sort by:</span>
                      <select value={localSortKey} onChange={e => setLocalSortKey(e.target.value as any)} className="border border-gray-300 rounded px-2 py-1 text-qr-dark">
                        <option value="title">Title</option>
                        <option value="artist">Artist</option>
                      </select>
                      <select value={localSortOrder} onChange={e => setLocalSortOrder(e.target.value as any)} className="border border-gray-300 rounded px-2 py-1 text-qr-dark">
                        <option value="asc">Asc</option>
                        <option value="desc">Desc</option>
                      </select>
                      <span className="text-gray-700 ml-3">Grouping:</span>
                      <select value={groupingMode} onChange={e => setGroupingMode(e.target.value as any)} className="border border-gray-300 rounded px-2 py-1 text-qr-dark">
                        <option value="letter">A–Z by {localSortKey === 'artist' ? 'Artist' : 'Title'}</option>
                        <option value="artistTitle">Artist › Title</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                    <div className="flex gap-1 overflow-x-auto max-w-[70%]">
                      {['#','A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'].map(ch => (
                        <button key={ch} onClick={() => jumpToLetter(ch)} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-qr-dark border border-gray-200">
                          {ch}
                        </button>
                      ))}
                    </div>
                  </div>

                    <div className="relative bg-white rounded border border-gray-200">
                    {/* Sticky overlay showing current section */}
                    {groupingMode !== 'none' && currentSectionKey && (
                      <div className="absolute top-0 left-0 right-0 z-10 bg-gray-100/95 text-gray-700 px-3 py-1 text-xs font-semibold border-b border-gray-200 rounded-t">
                        {currentSectionKey}
                      </div>
                    )}
                    {localError ? (
                      <div className="p-3 text-sm text-red-700 bg-red-50 border-b border-red-200">{localError}</div>
                    ) : VirtualList ? (
                      <VirtualList
                        ref={listRef}
                        height={384}
                        itemCount={rows.length}
                        itemSize={ITEM_SIZE}
                        width={'100%'}
                        onItemsRendered={onItemsRendered}
                      >
                        {({ index, style }: any) => {
                          const row = rows[index];
                          if (row.kind === 'header') {
                            return (
                              <div style={style} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold border-b border-gray-200">
                                {row.key}
                              </div>
                            );
                          }
                          const e = row.entry;
                          return (
                            <div style={style} className="flex items-center gap-3 px-3 py-3 border-b border-gray-100">
                              <input
                                type="checkbox"
                                checked={!!localSelected[e.id]}
                                onChange={(ev) => setLocalSelected(prev => ({ ...prev, [e.id]: ev.target.checked }))}
                                className="h-4 w-4"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-qr-dark font-medium truncate">{e.title}</div>
                                <div className="text-gray-600 text-sm truncate">{e.artist || 'Unknown Artist'}{e.durationSeconds ? ` • ${formattedDuration(e.durationSeconds)}` : ''}</div>
                              </div>
                              <button
                                onClick={() => handleAddLocalSingle(e)}
                                disabled={!singerName.trim()}
                                className="bg-qr-primary hover:bg-qr-secondary disabled:bg-gray-300 px-3 py-2 rounded-lg text-white text-sm"
                              >
                                Add
                              </button>
                            </div>
                          );
                        }}
                      </VirtualList>
                    ) : (
                      <div className="max-h-96 overflow-y-auto">
                        {rows.slice(0, 500).map((row, idx) => (
                          row.kind === 'header' ? (
                            <div key={`h-${idx}`} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold border-b border-gray-200">{row.key}</div>
                          ) : (
                            <div key={`i-${(row as any).entry.id}`} className="flex items-center gap-3 px-3 py-3 border-b border-gray-100">
                              <input
                                type="checkbox"
                                checked={!!localSelected[(row as any).entry.id]}
                                onChange={(ev) => setLocalSelected(prev => ({ ...prev, [(row as any).entry.id]: ev.target.checked }))}
                                className="h-4 w-4"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-qr-dark font-medium truncate">{(row as any).entry.title}</div>
                                <div className="text-gray-600 text-sm truncate">{(row as any).entry.artist || 'Unknown Artist'}{(row as any).entry.durationSeconds ? ` • ${formattedDuration((row as any).entry.durationSeconds)}` : ''}</div>
                              </div>
                              <button
                                onClick={() => handleAddLocalSingle((row as any).entry)}
                                disabled={!singerName.trim()}
                                className="bg-qr-primary hover:bg-qr-secondary disabled:bg-gray-300 px-3 py-2 rounded-lg text-white text-sm"
                              >
                                Add
                              </button>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {/* Trending Songs Tab */}
        {activeTab === 'trending' && (
          <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-300 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-orange-900 flex items-center">
                <span className="text-3xl mr-2">🔥</span>
                Hot This Week
              </h3>
              <button
                onClick={loadTrendingSongs}
                disabled={loadingTrending}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                {loadingTrending ? 'Loading...' : '🔄 Refresh'}
              </button>
            </div>

            {loadingTrending ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-orange-600"></div>
                <span className="ml-3 text-orange-700">Loading trending songs...</span>
              </div>
            ) : trendingSongs.length === 0 ? (
              <div className="text-center py-8 text-orange-700">
                <p className="text-4xl mb-2">🎵</p>
                <p>No trending data yet. Start singing to see what's hot!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {trendingSongs.map((song, index) => (
                  <div
                    key={song.songId}
                    className="bg-white border border-orange-200 rounded-lg p-4 hover:border-orange-400 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="text-2xl font-bold text-orange-600 w-8">
                        #{index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-orange-900">{song.title}</p>
                        {song.artist && (
                          <p className="text-orange-700 text-sm">{song.artist}</p>
                        )}
                        <p className="text-orange-600 text-xs mt-1">
                          🔥 {song.count} {song.count === 1 ? 'performance' : 'performances'} this week
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          if (!singerName.trim()) {
                            toast('Please enter your name first!', 'warning');
                            return;
                          }
                          await handleAddSong({
                            id: song.videoId || song.songId,
                            videoId: song.videoId,
                            title: song.title,
                            artist: song.artist,
                            source: song.source
                          });
                        }}
                        disabled={!singerName.trim()}
                        className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        Add to Queue
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-purple-300 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-purple-900 flex items-center">
                <span className="text-3xl mr-2">✨</span>
                Recommended For You
              </h3>
              <button
                onClick={loadRecommendations}
                disabled={loadingRecommendations}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                {loadingRecommendations ? 'Loading...' : '🔄 Refresh'}
              </button>
            </div>

            {loadingRecommendations ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600"></div>
                <span className="ml-3 text-purple-700">Finding songs you'll love...</span>
              </div>
            ) : recommendations.length === 0 ? (
              <div className="text-center py-8 text-purple-700">
                <p className="text-4xl mb-2">🎤</p>
                <p className="font-medium mb-2">No recommendations yet</p>
                <p className="text-sm">Sing a few songs first, and we'll suggest more you might like!</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-purple-700 mb-4">
                  Based on your singing history, here are songs that other singers enjoyed:
                </p>
                {recommendations.map((song) => (
                  <div
                    key={song.songId}
                    className="bg-white border border-purple-200 rounded-lg p-4 hover:border-purple-400 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-1">
                        <p className="font-bold text-purple-900">{song.title}</p>
                        {song.artist && (
                          <p className="text-purple-700 text-sm">{song.artist}</p>
                        )}
                        <p className="text-purple-600 text-xs mt-1">
                          ✨ {song.recommendationScore} {song.recommendationScore === 1 ? 'singer' : 'singers'} who sang similar songs also sang this
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          if (!singerName.trim()) {
                            toast('Please enter your name first!', 'warning');
                            return;
                          }
                          await handleAddSong({
                            id: song.videoId || song.songId,
                            videoId: song.videoId,
                            title: song.title,
                            artist: song.artist,
                            source: song.source
                          });
                        }}
                        disabled={!singerName.trim()}
                        className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        Add to Queue
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Current Queue Display */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <h3 className="text-xl font-display font-bold mb-4">Current Queue ({songQueue.length} songs)</h3>
          {songQueue.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {songQueue.map((song, index) => (
                <div key={song.id} className="flex items-center space-x-3 p-3 bg-qr-surface rounded-lg">
                  <span className="text-qr-accent font-bold w-8">#{index + 1}</span>
                  {song.thumbnail && (
                    <img 
                      src={song.thumbnail} 
                      alt={song.title}
                      className="w-10 h-10 rounded object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-qr-dark text-sm">{song.title}</p>
                    <p className="text-gray-600 text-xs">Singer: {song.singerName}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600">No songs in queue yet. Be the first to add one!</p>
          )}
        </div>

        {/* Confirm multiple selection modal */}
        {showConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
              <h4 className="text-xl font-semibold mb-2">Confirm Requests</h4>
              <p className="text-sm text-gray-700 mb-4">You are about to add the following songs for <strong>{singerName || 'Unknown Singer'}</strong>:</p>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded mb-4">
                {Object.entries(localSelected)
                  .filter(([,v]) => v)
                  .map(([id]) => localCatalog.find(e => e.id === id))
                  .filter(Boolean)
                  .map((e) => (
                    <div key={e!.id} className="px-3 py-2 border-b border-gray-100 text-sm">
                      <div className="font-medium text-qr-dark">{e!.title}</div>
                      <div className="text-gray-600">{e!.artist || 'Unknown Artist'} {e!.durationSeconds ? `• ${formattedDuration(e!.durationSeconds)}` : ''}</div>
                    </div>
                  ))}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowConfirm(false)} className="px-4 py-2 rounded bg-gray-200 text-qr-dark hover:bg-gray-300">Cancel</button>
                <button onClick={confirmAddLocalSelected} className="px-4 py-2 rounded bg-qr-primary text-white hover:bg-qr-secondary">Confirm</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;